/**
 * Client helpers for member-only S3 / same-origin media proxy URLs.
 */

export const S3_MEDIA_LOGIN_MESSAGE =
  'Sign in to access gallery and member media content.';

const AUTH_TOKEN_KEY = 'authToken';

/** Same-origin paths streamed from private S3 in dev (setupProxy). */
const PROTECTED_PATH_PREFIXES = [
  '/data/gallery/studio/musicians/audio/',
  '/data/gallery/studio/players/audio/',
  '/data/crafts/leather-diy/docs/',
  '/data/crafts/woodwork-diy/docs/',
  '/data/crafts/woodwork-diy/gallery/',
  '/data/crafts/woodwork-diy/covers/',
  '/__dev/studio-collection/',
  '/__dev/studio-playlists/',
  '/__dev/studio-theater/',
  '/__dev/studio-theater-s3',
  '/__dev/studio-theater-stream/',
  '/__dev/leather-diy-s3',
  '/__dev/woodwork-diy-s3',
  '/__dev/small-boats-s3',
  '/data/crafts/small-boats/',
  '/mgv-local-gallery/',
];

const PROTECTED_ROUTE_PREFIXES = ['/__dev/fsi-'];

export function getStoredAuthToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function isSignedInUser(auth, token) {
  return Boolean(auth && token);
}

export function isProtectedMediaPath(pathname) {
  const path = String(pathname || '');
  if (!path) return false;
  if (PROTECTED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  if (PROTECTED_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  if (path.includes('-s3') && path.startsWith('/__dev/')) return true;
  return false;
}

export function needsMediaAccessToken(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  if (raw.startsWith('/')) return isProtectedMediaPath(raw.split('?')[0]);
  try {
    if (typeof window !== 'undefined') {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.origin !== window.location.origin) return false;
      return isProtectedMediaPath(parsed.pathname);
    }
  } catch {
    return false;
  }
  return false;
}

/** Post-login return path from RequireAuth state or dev-proxy ?from= query. */
export function resolveAuthReturnPath(location) {
  const fromState = location?.state?.from;
  if (typeof fromState === 'string' && fromState.startsWith('/')) return fromState;
  try {
    const params = new URLSearchParams(location?.search || '');
    const fromQuery = params.get('from');
    if (typeof fromQuery === 'string' && fromQuery.startsWith('/')) return fromQuery;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Append session token for same-origin S3 dev proxies (audio/img/pdf tags).
 * @param {string} url
 * @param {string} [token]
 */
export function withMediaAccessToken(url, token = getStoredAuthToken()) {
  const raw = String(url || '').trim();
  if (!raw || !token || !needsMediaAccessToken(raw)) return raw;

  try {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(raw, base);
    if (parsed.origin !== base) return raw;
    if (parsed.searchParams.get('access_token') === token) return raw;
    parsed.searchParams.set('access_token', token);
    if (raw.startsWith('/')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}access_token=${encodeURIComponent(token)}`;
  }
}
